import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import authSession from "../lib/authSession";
import { useUserAuth } from "../store/user";
import GoogleOneTap from "./GoogleOneTap";
import { useToast } from "./ui/use-toast";

/**
 * Google One Tap popup for the public (marketing) pages.
 *
 * Renders the REAL Google Identity Services One Tap (via `GoogleOneTap`),
 * which shows the Google accounts signed into the browser and hands the
 * chosen credential to `userGoogleLogin` → `POST /api/auth/google`. On
 * success the store flips `userIsAuthenticated` and the landing page swaps
 * to the authenticated experience.
 *
 * Behaviour:
 *   • appears shortly after the page loads, on the top-right (Google's own UI)
 *   • only ever shows on public pages — never the auth entry pages
 *   • renders nothing when `REACT_APP_GOOGLE_CLIENT_ID` is not configured
 */

// Auth entry pages keep their own Google button — no popup there.
const AUTH_ENTRY_PATHS = new Set([
  "/login",
  "/forgot-password",
  "/reset-password",
  "/register",
  "/verify-email",
  "/adminlogin",
  "/staff/login",
  "/staff/reset-password",
  "/resource-personlogin",
]);

/** Learner auth is kept in the current tab; Zustand itself resets on reload. */
function hasRememberedLearnerSession() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem("fillop_session") || "null");
    if (stored?.token && (!stored.role || stored.role === "user")) return true;
  } catch {
    // Ignore unavailable or malformed session storage.
  }

  try {
    return (
      window.localStorage.getItem("isAdmin") === "false" &&
      Boolean(window.localStorage.getItem("uemail") || window.localStorage.getItem("email"))
    );
  } catch {
    return false;
  }
}

export default function GoogleOneTapPublicPrompt() {
  const location = useLocation();
  const { userIsAuthenticated, userGoogleLogin } = useUserAuth();
  const { toast } = useToast();

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  const enabled = useMemo(() => {
    // Once signed in the popup no longer belongs on the page.
    if (userIsAuthenticated || authSession.isAuthenticatedFor("user") || hasRememberedLearnerSession()) return false;
    const path = String(location.pathname).split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
    // Checkout presents its own Google-first sign-in dialog so the selected
    // cart item can resume automatically after authentication.
    if (path === "/cart") return false;
    if (AUTH_ENTRY_PATHS.has(path)) return false;
    // Public marketing routes — `/courses/:id` is reachable logged-out too.
    return authSession.isPublicPath(path) || path.startsWith("/courses/");
  }, [location.pathname, userIsAuthenticated]);

  // Exchange the credential at POST /api/auth/google. On failure the store
  // holds the error message — surface it so the user can retry.
  const handleGoogleSuccess = useCallback(async (response) => {
    const result = await userGoogleLogin(response.credential);
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Google sign-in failed",
        description: result.error || "Please try again.",
      });
    }
  }, [userGoogleLogin, toast]);

  const handleGoogleError = useCallback((err) => {
    // Missing client ID / script failure — only surface in dev to avoid
    // spamming public-page visitors with a config error.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[GoogleOneTap]", err?.message || err);
    }
  }, []);

  if (!googleClientId || !enabled) return null;

  return (
    <GoogleOneTap
      onSuccess={handleGoogleSuccess}
      onError={handleGoogleError}
      enabled
      autoPrompt
      renderButton={false}
    />
  );
}
